// Shared design atoms ported verbatim from the CitiDev design prototype
// (design/project/scripts/icons.jsx + ui.jsx). These power the app shell,
// feed rows, and right-rail widgets so the production app matches the
// prototype pixel-for-pixel.
import type { ReactElement, ReactNode, SVGProps } from "react";
import { CharacterAvatar } from "@/features/avatar/CharacterAvatar";
import type { AvatarConfig } from "@/features/avatar/catalog";

// ---------------------------------------------------------------------------
// Icons — 24x24 stroke icons, mirror design/project/scripts/icons.jsx
// ---------------------------------------------------------------------------

interface IcoProps {
  size?: number;
  sw?: number;
  fill?: string;
  children: ReactNode;
  extra?: SVGProps<SVGSVGElement>;
}

function Svg({ size = 18, sw = 1.7, fill = "none", children, extra }: IcoProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...extra}
    >
      {children}
    </svg>
  );
}

interface SizeProps {
  size?: number;
  style?: React.CSSProperties;
}

export const Icon = {
  feed: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </Svg>
  ),
  layout: ({ size }: SizeProps) => (
    <Svg size={size}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
    </Svg>
  ),
  project: ({ size, style }: SizeProps) => (
    <Svg size={size} extra={{ style }}>
      <path d="M4 7h16v12H4zM4 7l2-3h6l2 3" />
      <path d="M9 13h6" />
    </Svg>
  ),
  book: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM4 19a2 2 0 0 1 2-2h13" />
    </Svg>
  ),
  chat: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l1-4.2A8 8 0 1 1 21 12z" />
    </Svg>
  ),
  link: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M9 15l6-6" />
      <path d="M10.5 6.5l1-1a4 4 0 0 1 6 6l-1 1M13.5 17.5l-1 1a4 4 0 0 1-6-6l1-1" />
    </Svg>
  ),
  search: ({ size }: SizeProps) => (
    <Svg size={size}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </Svg>
  ),
  up: ({ size }: SizeProps) => (
    <Svg size={size} sw={2.2} fill="currentColor" extra={{ stroke: "none" }}>
      <path d="M12 5l7 8H5z" />
    </Svg>
  ),
  comment: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M21 11.5a7.5 7.5 0 0 1-10.8 6.7L4 19l1-4.2A7.5 7.5 0 1 1 21 11.5z" />
    </Svg>
  ),
  plus: ({ size }: SizeProps) => (
    <Svg size={size} sw={2.1}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  arrowR: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M9 5l7 7-7 7" />
    </Svg>
  ),
  arrowL: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M15 5l-7 7 7 7" />
    </Svg>
  ),
  share: ({ size }: SizeProps) => (
    <Svg size={size}>
      <circle cx="6" cy="12" r="2.4" />
      <circle cx="17" cy="6" r="2.4" />
      <circle cx="17" cy="18" r="2.4" />
      <path d="M8.1 11 14.9 7.2M8.1 13l6.8 3.8" />
    </Svg>
  ),
  x: ({ size }: SizeProps) => (
    <Svg size={size} sw={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  ),
  pin: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M12 17v5M9 3h6l-1 6 3 3H7l3-3z" />
    </Svg>
  ),
  calendar: ({ size, style }: SizeProps) => (
    <Svg size={size} extra={{ style }}>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9h16M8 3v4M16 3v4" />
    </Svg>
  ),
  trophy: ({ size, style }: SizeProps) => (
    <Svg size={size} extra={{ style }}>
      <path d="M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M9 16h6M10 16l-.5 4h5l-.5-4" />
    </Svg>
  ),
  external: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M14 4h6v6M20 4l-9 9M18 13v6H5V6h6" />
    </Svg>
  ),
  package: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M12 3 4 7v10l8 4 8-4V7zM4 7l8 4 8-4M12 11v10" />
    </Svg>
  ),
  moon: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M21 12.6A8.4 8.4 0 0 1 11.4 3 7.2 7.2 0 1 0 21 12.6z" />
    </Svg>
  ),
  sun: ({ size }: SizeProps) => (
    <Svg size={size}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2v2.6M12 19.4V22M2 12h2.6M19.4 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </Svg>
  ),
  shield: ({ size }: SizeProps) => (
    <Svg size={size}>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  ),
  flame: ({ size }: SizeProps) => (
    <Svg size={size} fill="currentColor" sw={0}>
      <path d="M12 2c.6 2.4-1.6 3.7-2.6 5.5C8.2 9.2 8 11 9 12.4c-1.4-.4-2.4-1.7-2.5-3.2C4.9 10.9 4 13 4 15c0 4.4 3.6 7 8 7s8-2.6 8-7c0-3-1.7-5.7-3.2-7.6.1 1.3-.4 2.5-1.3 3.3.4-2.7-.9-5.2-3.5-6.7z" />
    </Svg>
  ),
  star: ({ size }: SizeProps) => (
    <Svg size={size} fill="currentColor" sw={0}>
      <path d="M12 3l2.4 5.9 6.3.5-4.8 4.1 1.5 6.2L12 16.9 6.6 19.7l1.5-6.2-4.8-4.1 6.3-.5z" />
    </Svg>
  ),
} as const;

// ---------------------------------------------------------------------------
// Brand mark — spacey orbit mark from icons.jsx BrandMark
// ---------------------------------------------------------------------------

export function BrandMark({ size = 42 }: { size?: number }): ReactElement {
  return (
    <span className="brand-mark" style={{ width: size, height: size, color: "var(--green-deep)" }}>
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <ellipse
          className="brand-mark-orbit"
          cx="24"
          cy="24"
          rx="19.5"
          ry="8.4"
          stroke="currentColor"
          strokeWidth="2.8"
          opacity="0.72"
          transform="rotate(-26 24 24)"
        />
        <g className="brand-mark-moon" style={{ transformOrigin: "24px 24px" }} transform="rotate(-26 24 24)">
          <circle cx="43.5" cy="24" r="3.2" fill="currentColor" />
        </g>
        <circle cx="24" cy="24" r="6" fill="currentColor" />
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// timeAgo — relative time. The prototype anchored "now" to a fixed seed date;
// production uses real wall-clock time.
// ---------------------------------------------------------------------------

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const d = Math.round(h / 24);
  if (s < 60) return "just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Avatar — deterministic color chip with member initials (ui.jsx Avatar)
// ---------------------------------------------------------------------------

const AV_COLORS = [
  "#57a838",
  "#1a6fa3",
  "#0f7d87",
  "#5b2d8e",
  "#bd1f6a",
  "#c2792a",
  "#3a7d5c",
  "#9a4f8a",
];

export function avColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface AvatarProps {
  id: string;
  initials?: string;
  name?: string;
  size?: number;
  /** When present, renders the member's customizable character avatar instead
   *  of the deterministic initials chip. */
  avatar?: AvatarConfig | null;
}

export function Avatar({ id, initials, name, size = 36, avatar }: AvatarProps): ReactElement {
  if (avatar) {
    return <CharacterAvatar avatar={avatar} size={size} title={name} />;
  }
  const fs = Math.round(size * 0.38);
  const label = initials ?? (name ? initialsFor(name) : "?");
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: fs, background: avColor(id) }}
      title={name}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Vote — left-side up-arrow + count control (ui.jsx Vote)
// ---------------------------------------------------------------------------

interface VoteProps {
  count: number;
  voted?: boolean;
  onToggle?: (e: React.MouseEvent) => void;
}

export function Vote({ count, voted = false, onToggle }: VoteProps): ReactElement {
  return (
    <div className="vote" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={voted ? "on" : ""}
        onClick={onToggle}
        aria-label="upvote"
      >
        <Icon.up size={18} />
      </button>
      <span className={"count" + (voted ? " on" : "")}>{count}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post-type metadata (icons.jsx TYPE_META)
// ---------------------------------------------------------------------------

export type DesignPostType = "project" | "post" | "learning" | "template";

export const TYPE_META: Record<
  DesignPostType,
  { label: string; icon: keyof typeof Icon }
> = {
  project: { label: "Project", icon: "project" },
  post: { label: "Post", icon: "chat" },
  learning: { label: "Learning", icon: "book" },
  template: { label: "Template", icon: "package" },
};
